/**
 * Image URL Utilities
 *
 * Constructs optimized CDN URLs for responsive images.
 * Supports both:
 * - S3 keys (e.g., "orgs/orgId/media/uuid/photo.jpg")
 * - Full URLs (with S3→CDN transformation)
 *
 * Features:
 * - Responsive srcset generation
 * - LQIP (Low Quality Image Placeholder) for blur-up effect
 * - Format auto-negotiation via CDN (AVIF/WebP/JPEG)
 */

/**
 * The widths we ask the CDN for.
 *
 * ⚠️ 1920 IS DELIBERATELY ABSENT, AND IS NOT A TYPO. The optimizer is a
 * Lambda@Edge origin-request function, whose timeout is hard-capped at 30s
 * (`image-cdn.tf`), and a 1920-wide AVIF encode of a 2560px source does not
 * finish inside it. Measured on prod 2026-09-11 across Sam, Holy Trinity and
 * Chroma: 12 of 98 images returned `503 LambdaExecutionError` at 30.1s for
 * `w=1920`, every one of them 2560x3413 or larger, while all 98 succeeded at
 * `w=1280`. The function's own log group agrees — 21 of 201 invocations over
 * 25s, max 30.0s, peak 806MB of 1024MB. CloudFront does not cache that 503, so
 * every visitor pays the full 30s and still gets no image.
 *
 * Stopping at 1280 costs an upscale on very wide viewports. It keeps the win:
 * those 12 images still drop from 10.9MB to 2.0MB (81%). Put 1920 back only
 * once the encode fits the limit — more memory, a lower AVIF `effort`, or a
 * pre-generated rendition — and re-measure before trusting it.
 */
const BREAKPOINTS = [320, 640, 960, 1280] as const

/** The widest the CDN can actually encode inside its 30s limit. */
export const MAX_IMAGE_WIDTH: number = BREAKPOINTS[BREAKPOINTS.length - 1]

/** The smallest breakpoint at or above `width`, capped at MAX_IMAGE_WIDTH. */
function snapWidth(width: number): number {
  return BREAKPOINTS.find((w) => w >= width) ?? MAX_IMAGE_WIDTH
}

export interface ImageUrlOptions {
  /** Target width in pixels */
  width?: number
  /** Image quality (1-100), defaults to 80 */
  quality?: number
  /** Output format - 'auto' lets CDN choose based on Accept header */
  format?: 'auto' | 'webp' | 'jpeg' | 'png' | 'avif'
}

/**
 * Extract S3 key from a Fugue Lab uploads URL
 */
function extractS3Key(url: string): string | null {
  // Handle: https://fugue-lab-*-files-*.s3.*.amazonaws.com/key
  const s3Pattern = /https:\/\/fugue-lab-[\w-]+-files-\d+\.s3\.[\w-]+\.amazonaws\.com\/(.+)$/
  const s3Match = url.match(s3Pattern)
  if (s3Match) return s3Match[1]

  // Handle generic S3 URLs
  const genericS3Match = url.match(/s3(?:\.[\w-]+)?\.amazonaws\.com\/(.+)$/)
  if (genericS3Match) return genericS3Match[1]

  // Handle CloudFront URLs (already on CDN)
  const cfMatch = url.match(/cloudfront\.net\/(.+)$/)
  if (cfMatch) return cfMatch[1]

  return null
}

/**
 * The key of a url that is ALREADY on this CDN, or null.
 *
 * ⚠️ WITHOUT THIS, EVERY STORED IMAGE WAS UNRESIZABLE. The media API hands out
 * full `${cdnBase}/${key}` urls (`resolveUrl` in media.handler — by design, so a
 * public payload is self-contained), and both writers copy that url into section
 * config: the editor's ImagePicker stores `item.src`, the seed's upload helper
 * stores `publicUrl`. So section content holds `https://images.fuguelab.app/…`,
 * never a bare key — measured 2026-09-11: 48 such urls across the prod fleet, 0
 * keys. `extractS3Key` only recognised S3 and cloudfront.net hosts, so for that
 * url `getImageUrl` returned it UNCHANGED and `generateSrcset` emitted the same
 * url at every width — a srcset that validates, renders, and resizes nothing.
 * The CDN itself resizes fine (`?w=640&f=auto` → 10 KB AVIF from a 49 KB original).
 *
 * Matched against THIS base only, on purpose. A url on a different host — another
 * environment's CDN, a hotlink — is left alone rather than rebased onto this one,
 * where the object may not exist. Any existing query is dropped so the options
 * replace it instead of doubling it.
 */
function keyOnCdn(url: string, cdnBase: string): string | null {
  const base = cdnBase.replace(/\/+$/, '')
  if (!url.startsWith(`${base}/`)) return null
  const key = url.slice(base.length + 1).split(/[?#]/)[0]
  return key || null
}

/**
 * The media KEY behind `src`, or null when `src` is not CDN media at all.
 *
 * ⚠️ THIS IS THE GATE `<Image>` RESIZES THROUGH, AND `getImageUrl` CANNOT BE.
 * `getImageUrl` treats any non-http string as a key, which is right for the
 * CMS primitives (they are only ever handed media) and wrong on a bespoke site,
 * where `/images/hero.webp` is the SITE'S OWN static asset. Passed through
 * `getImageUrl` it becomes `${cdnBase}//images/hero.webp` — a path that does not
 * exist on the image CDN, i.e. a broken image that returns an error page.
 *
 * So only two shapes count as media:
 *   - an absolute url on THIS base (what section config actually stores), and
 *   - a bare `orgs/…` storage key.
 * Everything else — site paths, other hosts, data: urls — returns null and is
 * rendered exactly as given.
 */
export function mediaKeyOf(src: string | undefined, cdnBase: string | undefined): string | null {
  if (!src || !cdnBase) return null
  const key = /^https?:\/\//i.test(src)
    ? keyOnCdn(src, cdnBase)
    : /^orgs\//.test(src.split(/[?#]/)[0])
      ? src.split(/[?#]/)[0]
      : null
  // Media, but not resizable: `?w=…&f=auto` would RASTERISE a vector (a logo
  // goes soft at every width) and freeze an animated GIF on its first frame.
  // Rendered exactly as given.
  if (key && /\.(svg|gif)$/i.test(key)) return null
  return key
}

/**
 * Build optimized URL with query parameters
 */
function buildOptimizedUrl(cdnBase: string, key: string, options: ImageUrlOptions): string {
  const params = new URLSearchParams()

  // Clamped HERE, not at the call sites, because a caller asking for more than
  // the CDN can encode gets a 30s 503 (see BREAKPOINTS) — and the call sites
  // that did ask are the worst ones: `hero.tsx` passes maxWidth={1920} into
  // `usePreloadImage`, which puts that url in a `<link rel=preload>`, and into
  // `CmsImage`, which uses it as the `src`. One chokepoint, no opt-out.
  //
  // Snapped UP to a breakpoint for the opposite reason: the optimizer IGNORES a
  // width outside its allowlist and encodes the full-size original. The LQIP
  // asked for w=16 and got a 66KB AVIF that took 26s on a cache miss, so every
  // photo sat as a blur for half a minute (Chroma /about, 2026-09-14).
  if (options.width) params.set('w', snapWidth(options.width).toString())
  if (options.quality) params.set('q', options.quality.toString())
  if (options.format) params.set('f', options.format)

  const queryString = params.toString()
  // Normalized here, not by callers: a trailing-slash base produced `base//key`
  // on EVERY path (key and absolute), found by the absolute-url test on 2026-09-11.
  const base = cdnBase.replace(/\/+$/, '')
  return `${base}/${key}${queryString ? `?${queryString}` : ''}`
}

/**
 * Get full image URL from key or existing URL
 *
 * @param src - S3 key (e.g., "orgs/orgId/media/uuid/photo.jpg") or full URL
 * @param cdnBase - CDN base URL (e.g., "https://images.dev.fuguelab.app")
 * @param options - Image optimization options
 * @returns Full optimized image URL
 */
export function getImageUrl(src: string | undefined, cdnBase?: string, options: ImageUrlOptions = {}): string {
  if (!src) return ''

  // If no CDN base, return src as-is
  if (!cdnBase) return src

  // If already a full URL, try to transform S3→CDN
  if (src.startsWith('http://') || src.startsWith('https://')) {
    const key = keyOnCdn(src, cdnBase) ?? extractS3Key(src)
    if (key) {
      return buildOptimizedUrl(cdnBase, key, options)
    }
    // Not an S3 URL, return as-is
    return src
  }

  // It's a key - construct the CDN URL
  return buildOptimizedUrl(cdnBase, src, options)
}

/**
 * Get LQIP (Low Quality Image Placeholder) URL
 *
 * Returns the smallest width the CDN will actually resize to, at low quality,
 * displayed blurred while the full image loads. Not 16px: the optimizer has no
 * such width and silently serves the full-size original instead.
 *
 * @param src - S3 key or URL
 * @param cdnBase - CDN base URL
 * @returns URL for the placeholder image
 */
export function getLqipUrl(src: string | undefined, cdnBase?: string): string {
  return getImageUrl(src, cdnBase, { width: BREAKPOINTS[0], quality: 30, format: 'auto' })
}

/**
 * Generate srcset string for responsive images
 *
 * Creates srcset with multiple widths for the browser to choose from
 * based on viewport and device pixel ratio.
 *
 * @param src - S3 key or URL
 * @param cdnBase - CDN base URL
 * @param maxWidth - Optional max width to limit srcset variants
 * @returns srcset string (e.g., "url?w=320 320w, url?w=640 640w, ...")
 *
 * @example
 * <img srcSet={generateSrcset(key, cdnBase)} sizes="100vw" />
 */
export function generateSrcset(src: string | undefined, cdnBase?: string, maxWidth?: number): string {
  if (!src || !cdnBase) return ''

  const widths = maxWidth ? BREAKPOINTS.filter((w) => w <= maxWidth) : BREAKPOINTS

  return widths.map((w) => `${getImageUrl(src, cdnBase, { width: w, format: 'auto' })} ${w}w`).join(', ')
}

/**
 * Generate responsive sizes attribute
 *
 * @param customSizes - Custom sizes object or use defaults
 * @returns sizes attribute string
 *
 * @example
 * // Full width on mobile, half on tablet, third on desktop
 * generateSizes() // "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
 */
export function generateSizes(customSizes?: Record<string, string>): string {
  const defaults: Record<string, string> = {
    '(max-width: 640px)': '100vw',
    '(max-width: 1024px)': '50vw',
    '': '33vw',
  }

  const sizes = customSizes || defaults
  return Object.entries(sizes)
    .map(([query, size]) => (query ? `${query} ${size}` : size))
    .join(', ')
}

/**
 * Derive a `sizes` attribute for an image rendered inside a responsive grid.
 *
 * Matches the mobile-first grids used by gallery/collection sections:
 * 1 column on mobile, 2 columns from `sm` (640px), and the configured
 * column count on desktop.
 *
 * @param columns - Desktop column count (2-6)
 * @returns sizes attribute string
 *
 * @example
 * gridSizes(3) // "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
 */
export function gridSizes(columns: number): string {
  const cols = Math.max(1, Math.floor(columns))
  if (cols <= 1) return '100vw'
  if (cols === 2) return '(max-width: 640px) 100vw, 50vw'
  const pct = Math.round(100 / cols)
  return `(max-width: 640px) 100vw, (max-width: 1024px) 50vw, ${pct}vw`
}
